import React from 'react';

import {
    Alert,
    Card,
    Collapse,
    Divider,
    Table,
    Tag,
    Typography,
} from 'antd';

import type { ColumnsType } from 'antd/es/table';

const { Title, Paragraph, Text, Link } = Typography;

const BASE_URL = 'https://monitor.agropogoda.com/api';
const PDF_URL = 'https://agropogoda.com/static/files/api-public.pdf';

interface ParamRow {
    name: string;
    required: string;
    description: string;
}

const metricsParams: ParamRow[] = [
    {
        name: 'number_in_db',
        required: 'да',
        description: 'ID станции из /get_all_zonds (поле NumberInDB)',
    },
    {
        name: 'from',
        required: 'да',
        description: 'Начало периода: YYYY-MM-DD (UTC), YYYY-MM-DDTHH:MM:SS или unixtime',
    },
    {
        name: 'to',
        required: 'да',
        description: 'Конец периода (те же форматы)',
    },
    {
        name: 'blocks',
        required: 'нет',
        description: 'metrics | helper | metrics,helper. По умолчанию metrics',
    },
    {
        name: 'format',
        required: 'нет',
        description: 'grouped (по умолчанию) | raw',
    },
    {
        name: 'aggregate',
        required: 'нет',
        description: 'hourly | daily — шаг агрегации',
    },
    {
        name: 'agg_func',
        required: 'с aggregate',
        description: 'avg | min | max | sum | count | std',
    },
    {
        name: 'limit',
        required: 'нет',
        description: 'Только с format=raw — сколько последних строк',
    },
];

const responseCodes = [
    { code: '200', meaning: 'OK' },
    { code: '400', meaning: 'Неверные параметры (invalid zond_number, empty date, bad format, bad aggregate, bad agg_func)' },
    { code: '401', meaning: 'Ключ отсутствует / невалидный / отозван / истёк' },
    { code: '402', meaning: 'Ключ валиден, но подписка не активна' },
    { code: '403', meaning: 'Ключ не имеет доступа к эндпоинту' },
    { code: '404', meaning: 'Станция не найдена' },
    { code: '429', meaning: 'Превышен rate limit или дневная квота' },
    { code: '500', meaning: 'Внутренняя ошибка сервиса' },
];

const curlExamples = [
    {
        title: 'Список всех станций',
        cmd: `curl -H "X-API-Key: $API_KEY" \\\n  "${BASE_URL}/get_all_zonds"`,
    },
    {
        title: 'Метрики за день (сырые точки)',
        cmd: `curl -H "X-API-Key: $API_KEY" \\\n  "${BASE_URL}/get_metrics?number_in_db=P0016&from=2026-10-13&to=2026-10-13"`,
    },
    {
        title: 'Средние за каждый день',
        cmd: `curl -H "X-API-Key: $API_KEY" \\\n  "${BASE_URL}/get_metrics?number_in_db=P0016&from=2026-10-01&to=2026-10-14&aggregate=daily&agg_func=avg"`,
    },
    {
        title: 'Метрики + агрорасчёты одним запросом',
        cmd: `curl -H "X-API-Key: $API_KEY" \\\n  "${BASE_URL}/get_metrics?number_in_db=P0016&from=2026-10-01&to=2026-10-14&blocks=metrics,helper"`,
    },
    {
        title: 'Сырые строки',
        cmd: `curl -H "X-API-Key: $API_KEY" \\\n  "${BASE_URL}/get_metrics?number_in_db=P0016&from=2026-10-13&to=2026-10-13&format=raw&limit=100"`,
    },
];

const paramColumns: ColumnsType<ParamRow> = [
    {
        title: 'Параметр',
        dataIndex: 'name',
        key: 'name',
        width: 160,
        render: (v: string) => <Text code>{v}</Text>,
    },
    {
        title: 'Обязательный',
        dataIndex: 'required',
        key: 'required',
        width: 120,
    },
    {
        title: 'Описание',
        dataIndex: 'description',
        key: 'description',
    },
];

const codeBlockStyle: React.CSSProperties = {
    background: '#f5f5f5',
    padding: 12,
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    margin: 0,
};

const ApiDocsPanel: React.FC = () => {
    return (
        <>
            <Card title="Подключение" style={{ maxWidth: 900 }}>
                <Paragraph>Базовый URL:</Paragraph>
                <Paragraph>
                    <Text code copyable>
                        {BASE_URL}
                    </Text>
                </Paragraph>

                <Paragraph style={{ marginTop: 16 }}>
                    Аутентификация — заголовок <Text code>X-API-Key</Text>:
                </Paragraph>
                <Paragraph>
                    <Text code copyable>
                        X-API-Key: agp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                    </Text>
                </Paragraph>

                <Paragraph style={{ marginTop: 16 }}>
                    Ключ даёт доступ ко всем данным тарифа AgroAPI: список станций
                    и метрики по любой из них. Параметры запроса{' '}
                    (<Text code>blocks</Text>, <Text code>format</Text>,{' '}
                    <Text code>aggregate</Text>, <Text code>limit</Text>) указываются
                    в каждом отдельном запросе.
                </Paragraph>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                    message="Полная документация"
                    description={
                        <span>
                            Все эндпоинты, параметры и примеры ответов — в&nbsp;
                            <Link href={PDF_URL} target="_blank" rel="noreferrer">
                                api-public.pdf
                            </Link>
                            .
                        </span>
                    }
                />
            </Card>

            <Card title="Эндпоинты" style={{ maxWidth: 900, marginTop: 16 }}>
                <Title level={5}>
                    <Text code>/get_all_zonds</Text> <Tag color="green">GET</Tag>
                </Title>
                <Paragraph type="secondary">
                    Список всех станций системы. Параметров нет.
                </Paragraph>
                <Paragraph>
                    <Text code copyable>
                        {BASE_URL}/get_all_zonds
                    </Text>
                </Paragraph>

                <Divider />

                <Title level={5}>
                    <Text code>/get_metrics</Text> <Tag color="green">GET</Tag>
                </Title>
                <Paragraph type="secondary">
                    Единый эндпоинт: метрики со станции, агрономические расчёты и
                    сырые строки — через параметры.
                </Paragraph>
                <Paragraph>
                    <Text code copyable>
                        {BASE_URL}/get_metrics
                    </Text>
                </Paragraph>

                <Table
                    rowKey="name"
                    columns={paramColumns}
                    dataSource={metricsParams}
                    pagination={false}
                    size="small"
                    style={{ marginTop: 12 }}
                />
            </Card>

            <Card title="Примеры curl" style={{ maxWidth: 900, marginTop: 16 }}>
                <Collapse
                    items={curlExamples.map((ex, i) => ({
                        key: String(i),
                        label: ex.title,
                        children: (
                            <Paragraph copyable={{ text: ex.cmd }} style={codeBlockStyle}>
                                {ex.cmd}
                            </Paragraph>
                        ),
                    }))}
                />
            </Card>

            <Card title="Коды ответов" style={{ maxWidth: 900, marginTop: 16 }}>
                <Table
                    rowKey="code"
                    columns={[
                        {
                            title: 'Код',
                            dataIndex: 'code',
                            width: 100,
                            render: (v: string) => <Text strong>{v}</Text>,
                        },
                        {
                            title: 'Значение',
                            dataIndex: 'meaning',
                        },
                    ]}
                    dataSource={responseCodes}
                    pagination={false}
                    size="small"
                />
            </Card>
        </>
    );
};

export default ApiDocsPanel;
